import { Rng } from '../core/rng';
import { bearingToX, bearingToZ, clamp, Vec3 } from '../core/math';
import { SHIP_CLASSES, type AmmoId, type DifficultyId, type ShipClassId } from '../data/classes';
import { SimEvents, type SimEvent } from './events';
import { WaveField, type SeaConditions } from './waves';
import { Wind } from './wind';
import { Weather, type WeatherKind } from './weather';
import { DayNight } from './daynight';
import type { Ship } from './ship';
import type { DeckCargo } from './deck';
import type { Battery } from './cannons';
import { Flotsam } from './floaters';
import { Vessel } from './vessel';
import { Ballistics } from './ballistics';
import { AIM_PARTS, aimHeight, type AimPart } from './gunnery';
import { Encounters, type GroupKind } from './encounters';
import { Contacts, boardable, castOff } from './contact';
import { hostile, power, type Battlefield } from './captain';
import { Lookout } from './lookout';
import { DevTools, type DevAction } from './dev';
import { PARTIES, type Party } from './crew';

/** Everything the player (or a test) can ask the sim to do this frame. */
export interface PlayerCommand {
  /** -1 (port) .. +1 (starboard), continuous. */
  helm: number;
  /** +1 set more sail, -1 shorten sail (edge-triggered). */
  sailDelta: number;
  /** Fire the port / starboard broadside, the guns as they're laid (edge-triggered). */
  firePort: boolean;
  fireStarboard: boolean;
  /**
   * Lay a side's guns on a point of the sea (world x, z) and keep them on it
   * while this stays set; null leaves them as they are. Held, not edge-triggered.
   * With `target`, they're laid on that ship instead - on a point of her
   * (ahead of her centre and to her starboard, in her own heading), led by
   * the time of flight; x, z is then only where to fall back to.
   */
  aim: PlayerAim | null;
  /** Toggle automatic sail trim. */
  toggleTrim: boolean;
  /** Manual trim input, -1..1, only used with auto trim off. */
  trim: number;
  /** Cycle the ammunition loaded next. */
  cycleAmmo: boolean;
  /** Throw grapnels at the nearest ship alongside. */
  board: boolean;
  /** In a boarding fight: call all hands to it, or send them back to the guns (toggle, edge-triggered). */
  allHands: boolean;
  /** Cut the grapnels: fall back to our own ship, or cast off boarders (edge-triggered). */
  cutLines: boolean;
  /** Order a damage party to work over the guns, or leave it to the officers again (toggle, edge-triggered). */
  order: Party | null;
  /** Serve out grog to put heart into the men (edge-triggered). */
  rally: boolean;
  /** Show or douse the lanterns. */
  toggleLights: boolean;
  /** Dev: move our ship to this world point (keeping heading and way). */
  teleport: { x: number; z: number } | null;
  /** Dev: test scenarios and tools to run this frame (see sim/dev.ts). */
  dev: DevAction[];
}

export interface PlayerAim {
  side: 1 | -1;
  x: number;
  z: number;
  target?: { id: number; ahead: number; right: number } | null;
  /** Where on a ship to lay them (over a bare mark, as if a ship of ours were there); the waterline if not given. */
  part?: AimPart;
}

/** The ship the player's guns are laid on: the point of her aimed at now (world, sea level), and where the guns lead it to. */
export interface AimTarget {
  id: number;
  x: number;
  z: number;
  leadX: number;
  leadZ: number;
}

export function emptyCommand(): PlayerCommand {
  return { helm: 0, sailDelta: 0, firePort: false, fireStarboard: false, aim: null, toggleTrim: false, trim: 0, cycleAmmo: false, board: false, allHands: false, cutLines: false, order: null, rally: false, toggleLights: false, teleport: null, dev: [] };
}

/**
 * Where a teleport to (x, z) would really put our ship: never on top of
 * another one (she comes up at a safe distance from it instead). `near` is
 * the ship she was kept off, if any.
 */
export function safeTeleportTarget(world: World, x: number, z: number): { x: number; z: number; near: Vessel | null } {
  const me = world.player;
  let near: Vessel | null = null;
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (const v of world.vessels) {
      if (v === me || v.ship.sunk) continue;
      // Clear of her hull, rigging and anything hanging over the side.
      const min = 60 + 22 * (me.ship.sx + v.ship.sx);
      let dx = x - v.ship.com.x, dz = z - v.ship.com.z;
      let d = Math.hypot(dx, dz);
      if (d >= min) continue;
      if (d < 1e-3) {
        // Dead on top of her: come up on the side we're coming from.
        dx = me.ship.com.x - v.ship.com.x;
        dz = me.ship.com.z - v.ship.com.z;
        d = Math.hypot(dx, dz);
        if (d < 1e-3) (dx = 1), (dz = 0), (d = 1);
      }
      x = v.ship.com.x + (dx / d) * min;
      z = v.ship.com.z + (dz / d) * min;
      near = v;
      moved = true;
    }
    if (!moved) break;
  }
  return { x, z, near };
}

export interface WorldSettings {
  /** Multiplier on wave height (user "sea state" slider). */
  waveIntensity: number;
  /** Override wind speed (m/s) or null to follow the weather. */
  windSpeed: number | null;
  /** Override wind "from" bearing (radians) or null to follow the weather. */
  windFrom: number | null;
  /** Dev: wind-sea height relative to the weather's, or null. */
  seaScale: number | null;
  /** Dev: swell significant height (m), or null for the weather's. */
  swellHeight: number | null;
  /** Dev: crest sharpness 0..1, or null for the weather's. */
  chop: number | null;
}

export interface WorldOptions {
  seed?: number;
  weather?: WeatherKind;
  hours?: number;
  windFrom?: number;
  heading?: number;
  plan?: number;
  playerClass?: ShipClassId;
  enemies?: boolean;
  difficulty?: DifficultyId;
}

export class World {
  static readonly DT = 1 / 60;

  readonly rng: Rng;
  readonly events = new SimEvents();
  readonly weather: Weather;
  readonly wind: Wind;
  readonly waves: WaveField;
  readonly day: DayNight;
  readonly vessels: Vessel[] = [];
  readonly player: Vessel;
  readonly ballistics: Ballistics;
  readonly encounters: Encounters;
  readonly contacts: Contacts;
  readonly flotsam: Flotsam;
  /** What our own lookouts have seen: the only ships the player knows about. */
  readonly lookout: Lookout;
  readonly settings: WorldSettings = { waveIntensity: 1, windSpeed: null, windFrom: null, seaScale: null, swellHeight: null, chop: null };
  /** Developer tools: test scenarios run on request through the command. */
  readonly dev: DevTools;
  readonly sea: SeaConditions = { windSpeed: 8, windX: 1, windZ: 0, swellHeight: 1, waveScale: 1, chop: 0.7 };

  time = 0;
  /** Interpolation factor between the last two sim steps, for rendering. */
  alpha = 1;
  /** Rain wetness of exposed surfaces, 0..1. */
  wetness = 0;
  /** Meteorological visibility, m. */
  visibility = 9000;
  /** Game speed: sim seconds per real second (0 pauses). Everything runs on it. */
  speed = 1;
  private accumulator = 0;
  private lastHalfHour = -1;
  private recent: SimEvent[] = [];
  private recentTimes: number[] = [];
  private readonly sunkTimers = new Map<number, number>();
  private stepCount = 0;

  constructor(opts: WorldOptions = {}) {
    this.rng = new Rng(opts.seed ?? 20260925);
    const windFrom = opts.windFrom ?? 1.05; // ENE trades
    this.weather = new Weather(new Rng(this.rng.int(1 << 30)), opts.weather ?? 'fair', windFrom);
    this.wind = new Wind(windFrom, this.weather.params.windSpeed);
    const toX = -bearingToX(windFrom), toZ = -bearingToZ(windFrom);
    this.waves = new WaveField(this.rng.int(1 << 30), Math.atan2(toZ, toX));
    this.day = new DayNight(opts.hours ?? 16.2);
    this.updateSea();
    this.waves.settle(this.sea);
    this.player = new Vessel(SHIP_CLASSES[opts.playerClass ?? 'frigate'], 'player', 'Tidebreaker', 0, 0, opts.heading ?? 3.0, this.rng.int(1 << 30), 'normal', opts.plan ?? 3);
    this.vessels.push(this.player);
    // Start under way.
    const h = this.player.ship.heading;
    this.player.ship.vel.set(Math.sin(h) * 3.2, 0, -Math.cos(h) * 3.2);
    this.ballistics = new Ballistics(this.rng.int(1 << 30));
    this.encounters = new Encounters(this.rng.int(1 << 30));
    this.encounters.enabled = opts.enemies ?? false;
    this.encounters.difficulty = opts.difficulty ?? 'normal';
    this.contacts = new Contacts(this.rng.int(1 << 30));
    this.flotsam = new Flotsam(new Rng(this.rng.int(1 << 30)), 26, 0, 0);
    this.lookout = new Lookout(this.rng.int(1 << 30));
    this.lastHalfHour = Math.floor(this.day.hours * 2);
    this.dev = new DevTools(this, this.rng.int(1 << 30), World.DT);
  }

  // Back-compatible handles on the player's ship.
  get ship(): Ship {
    return this.player.ship;
  }
  get battery(): Battery {
    return this.player.battery;
  }
  get cargo(): DeckCargo {
    return this.player.cargo;
  }

  /** Add a ship with an AI captain (tests, debugging, scenarios). */
  spawn(kind: GroupKind, distance?: number): Vessel[] {
    const vs = this.encounters.spawn(kind, this.player, distance);
    this.vessels.push(...vs);
    return vs;
  }

  /** Advance by a real frame time using fixed steps; returns render alpha. */
  update(frameDt: number, cmd: PlayerCommand): number {
    this.applyCommand(cmd);
    this.accumulator += Math.min(frameDt, 0.25) * this.speed;
    let steps = 0;
    // Never more than a few steps a frame (more when running fast): a slow
    // frame slows the world rather than spiralling.
    const cap = Math.min(40, Math.ceil(4 * Math.max(1, this.speed)));
    while (this.accumulator >= World.DT && steps < cap) {
      this.step(World.DT, cmd);
      this.accumulator -= World.DT;
      steps++;
    }
    if (steps === cap) this.accumulator = 0;
    this.alpha = clamp(this.accumulator / World.DT, 0, 1);
    return this.alpha;
  }

  /**
   * The swing of the fight in each crew's heart: their shot striking home
   * cheers them and takes it out of the ship struck (a gun dismounted, a
   * rake, a mast brought down, colours struck most of all).
   */
  private spirits(events: readonly SimEvent[]): void {
    const find = (id: number | undefined) => (id === undefined || id < 0 ? undefined : this.vessels.find((v) => v.id === id));
    const swing = (by: number | undefined, at: number | undefined, feel: number) => {
      const a = find(by), b = find(at);
      if (!a || !b || a === b) return;
      a.cheer(feel * 0.1, feel);
      b.crew.momentum -= feel;
    };
    for (const e of events) {
      switch (e.type) {
        case 'impact': {
          const w = e.what === 'gun' ? 0.12 : e.what === 'mast' || e.what === 'rudder' ? 0.06 : e.what === 'hull' || e.what === 'rail' ? 0.035 * clamp(e.energy, 0.3, 1.5) : 0;
          if (w > 0) swing(e.by, e.vessel, w);
          break;
        }
        case 'raked': swing(e.by, e.vessel, 0.15); break;
        case 'mastFall': swing(find(e.vessel)?.lastHitBy, e.vessel, e.level === 3 ? 0.7 : 0.3); break;
        case 'struck': swing(find(e.vessel)?.lastHitBy, e.vessel, 1); break;
        default: break;
      }
    }
  }

  private applyCommand(cmd: PlayerCommand): void {
    const p = this.player;
    const rig = p.ship.sails;
    if (p.fighting) {
      if (cmd.sailDelta !== 0) rig.setPlan(clamp(rig.plan + cmd.sailDelta, 0, rig.plans.length - 1));
      if (cmd.toggleTrim) rig.autoTrim = !rig.autoTrim;
      rig.manualTrim = clamp(cmd.trim, -1, 1);
      if (cmd.firePort) this.orderFire(-1);
      if (cmd.fireStarboard) this.orderFire(1);
      if (cmd.cycleAmmo) {
        const carried = p.cls.ammo;
        p.battery.ammo = carried[(carried.indexOf(p.battery.ammo) + 1) % carried.length] as AmmoId;
      }
      if (cmd.board) this.orderBoard();
      if (cmd.allHands && p.boardingWith >= 0) p.allHands = !p.allHands;
      if (cmd.cutLines && p.boardingWith >= 0 && !this.contacts.cut(p, this.vessels, this.events)) this.events.push({ type: 'cutRefused', vessel: p.id });
      if (cmd.order && PARTIES.includes(cmd.order)) p.order(cmd.order);
      if (cmd.rally) p.rally(this.events);
    }
    const aim = cmd.aim;
    const t = aim?.target;
    const target = t && Number.isFinite(t.ahead) && Number.isFinite(t.right) ? { id: t.id, ahead: t.ahead, right: t.right } : null;
    const part = aim && AIM_PARTS.includes(aim.part as AimPart) ? aim.part : 'waterline';
    this.playerAim = p.fighting && aim && Number.isFinite(aim.x) && Number.isFinite(aim.z) ? { side: aim.side, x: aim.x, z: aim.z, target, part } : null;
    if (cmd.toggleLights) p.lights = !p.lights;
    if (cmd.teleport) this.teleportPlayer(cmd.teleport.x, cmd.teleport.z);
    cmd.teleport = null;
    for (const a of cmd.dev) this.dev.apply(a);
    cmd.dev.length = 0;
    cmd.sailDelta = 0;
    cmd.firePort = false;
    cmd.fireStarboard = false;
    cmd.toggleTrim = false;
    cmd.cycleAmmo = false;
    cmd.board = false;
    cmd.allHands = false;
    cmd.cutLines = false;
    cmd.order = null;
    cmd.rally = false;
    cmd.toggleLights = false;
  }

  /**
   * Fire a broadside (-1 port, +1 starboard). The order beats to quarters;
   * it's carried out once the gun crews are in place (or given up after a while).
   */
  orderFire(side: 1 | -1): void {
    if (!this.player.fighting) return;
    const i = side < 0 ? 0 : 1;
    this.fireOrder[i] = 12;
    this.firing[i] = false;
    this.quarters = 120;
  }

  /** Throw grapnels at the nearest enemy alongside (for a few seconds, until they take). */
  orderBoard(): void {
    if (this.player.fighting) this.playerBoard = 4;
  }

  /** Dev: move our ship bodily to (x, z), keeping her heading and way. */
  private teleportPlayer(tx: number, tz: number): void {
    const p = this.player, s = p.ship;
    if (s.sunk) return;
    const { x, z } = safeTeleportTarget(this, tx, tz);
    const dx = x - s.com.x, dz = z - s.com.z;
    // Riding the sea there as she rode it here (heave, not a drop or a lift).
    const dy = this.waves.heightAt(x, z) - this.waves.heightAt(s.com.x, s.com.z);
    s.translate(dx, dy, dz);
    // Any grapnels part (they would otherwise haul the ships together from
    // wherever she is now).
    castOff(p, this.vessels);
    // Her own people in the water come too.
    for (const m of p.crew.members) {
      if (m.onBoard) continue;
      m.wx += dx;
      m.wy += dy;
      m.wz += dz;
    }
    this.events.push({ type: 'teleport', vessel: p.id, x: s.com.x, z: s.com.z, dx, dy, dz });
  }

  /** Seconds left on the player's order to board. */
  private playerBoard = 0;
  /** Where the player is laying a broadside, if anywhere. */
  private playerAim: PlayerAim | null = null;
  /** The ship the player's guns are laid on, if they're laid on one (for the view and HUD). */
  aimTarget: AimTarget | null = null;
  /** Her way, smoothed over a second or so (the guns lead her course, not her yawing on the swell). */
  private readonly leadVel = { id: -1, x: 0, z: 0 };
  /** Outstanding fire orders (port, starboard), seconds before they lapse. */
  private readonly fireOrder = [0, 0];
  /** The broadside has begun: the rest fire as their crews come up. */
  private readonly firing = [false, false];
  /** Seconds the player's crew stay at quarters without an enemy in sight. */
  quarters = 0;

  /**
   * Where to lay the guns to hit a point of a ship under way: where that
   * point will be when the shot gets there. The time of flight is the guns'
   * own from the step before (it changes slowly, so this settles in a few
   * steps); the shot already carries our own way, so it's her way over the
   * ground that's led. Null if she's gone.
   */
  private leadOn(t: { id: number; ahead: number; right: number }, dt: number): AimTarget | null {
    const v = this.vessels.find((u) => u.id === t.id);
    if (!v || v === this.player || !v.alive || v.ship.sunk) return null;
    const s = v.ship;
    const h = s.heading, fx = Math.sin(h), fz = -Math.cos(h);
    const x = s.com.x + fx * t.ahead - fz * t.right;
    const z = s.com.z + fz * t.ahead + fx * t.right;
    const lv = this.leadVel;
    if (lv.id !== v.id) Object.assign(lv, { id: v.id, x: s.vel.x, z: s.vel.z });
    const k = 1 - Math.exp(-dt / 1.2);
    lv.x += (s.vel.x - lv.x) * k;
    lv.z += (s.vel.z - lv.z) * k;
    const me = this.player.ship.com;
    const prior = this.player.battery.aim;
    const tof = prior && Number.isFinite(prior.tof) && prior.tof > 0 ? prior.tof : Math.hypot(x - me.x, z - me.z) / 420;
    return { id: v.id, x, z, leadX: x + lv.x * tof, leadZ: z + lv.z * tof };
  }

  private updateSea(): void {
    const w = this.weather.params;
    const s = this.settings;
    this.sea.windSpeed = this.wind.meanSpeed;
    // Waves follow the mean wind direction, not every gust.
    this.sea.windX = -bearingToX(this.wind.meanFrom);
    this.sea.windZ = -bearingToZ(this.wind.meanFrom);
    this.sea.swellHeight = (s.swellHeight ?? w.swellHeight) * Math.sqrt(s.waveIntensity);
    this.sea.waveScale = w.waveScale * (s.seaScale ?? 1) * s.waveIntensity;
    this.sea.chop = s.chop ?? w.chop;
    // Koschmieder: visibility from haze, fog and rain extinction.
    const ext = 3.2e-5 + w.haze * 2.4e-4 + w.rain * 3e-4 + w.fog * w.fog * 0.012 + w.rain * 9e-4;
    this.visibility = clamp(3.9 / ext, 150, 14000);
  }

  /**
   * Jump straight to the sea (and mean wind) the weather and settings call
   * for now, rather than letting it build up or die away over a minute or two.
   */
  settleSea(): void {
    const w = this.weather.params;
    this.wind.meanSpeed = this.settings.windSpeed ?? w.windSpeed;
    this.wind.meanFrom = this.settings.windFrom ?? this.weather.windFrom;
    this.updateSea();
    this.waves.settle(this.sea);
  }

  step(dt: number, cmd: PlayerCommand): void {
    this.time += dt;
    this.day.update(dt);
    const player = this.player;
    this.weather.update(dt, player.ship.com.x, player.ship.com.z, this.events);
    const w = this.weather.params;
    this.wind.update(dt, this.settings.windSpeed ?? w.windSpeed, this.settings.windFrom ?? this.weather.windFrom, w.gustiness);
    this.updateSea();
    this.waves.update(dt, this.sea);

    const mark = this.events.mark;
    const bf: Battlefield = {
      vessels: this.vessels, time: this.time, wind: this.wind, visibility: this.visibility, daylight: this.day.daylight,
      seaState: this.waves.significantHeight, events: this.events, recent: this.recent, attackers: this.attackerMap(),
    };
    this.playerSituation();
    // The player's guns, laid on her mark (the crews stay at quarters and man that side).
    const aim = player.fighting ? this.playerAim : null;
    this.aimTarget = aim?.target ? this.leadOn(aim.target, dt) : null;
    if (aim) {
      this.quarters = Math.max(this.quarters, 20);
      player.battle = true;
      player.gunSide = aim.side;
      const t = this.aimTarget;
      const sy = t ? this.vessels.find((v) => v.id === t.id)!.ship.sy : player.ship.sy;
      player.battery.layOn(aim.side, t ? t.leadX : aim.x, t ? t.leadZ : aim.z, dt, player.ship, this.wind, aimHeight(aim.part ?? 'waterline', sy));
    } else {
      player.battery.standDown();
    }
    const ctx = { waves: this.waves, wind: this.wind, ballistics: this.ballistics, events: this.events, rain: w.rain, time: this.time };
    this.stepCount++;
    for (const v of this.vessels) {
      if (!v.alive) continue;
      // Level of detail: ships far from ours are simulated at a coarser
      // step (the hull integrator is stable to 1/15 s even in a storm) and
      // their cloth less often.
      const d = Math.hypot(v.ship.com.x - player.ship.com.x, v.ship.com.z - player.ship.com.z);
      const every = v === player ? 1 : d > 2500 ? 4 : d > 700 ? 2 : 1;
      if ((this.stepCount + v.id) % every !== 0) continue;
      const vdt = dt * every;
      const helm = v === player ? (player.fighting || player.state === 'sinking' ? cmd.helm : 0) : v.captain ? v.captain.update(vdt, bf) : 0;
      v.ship.sails.clothSkip = v === player ? 0 : d > 1500 ? 3 : d > 250 ? 1 : 0;
      v.ship.sails.clothCoarse = v !== player && d > 250;
      v.update(vdt, ctx, helm);
      v.lights = v === player ? v.lights : this.day.daylight < 0.5;
    }
    this.dev.update(dt);
    this.ballistics.update(dt, this.vessels, this.waves, this.wind, this.events);
    this.playerBoard = Math.max(0, this.playerBoard - dt);
    this.quarters = Math.max(0, this.quarters - dt);
    for (const [i, side] of [[0, -1], [1, 1]] as const) {
      if (this.fireOrder[i]! <= 0) continue;
      this.fireOrder[i]! -= dt;
      const manned = player.battery.guns.filter((g) => g.spec.side === side && g.loaded && !g.destroyed && g.fireAt < 0 && g.crew > 0.2).length;
      const loaded = player.battery.ready(side);
      if (this.firing[i]) {
        if (manned > 0) player.battery.fire(side);
        if (loaded === 0) this.fireOrder[i] = 0;
      } else if (manned > 0 && (manned >= loaded * 0.75 || this.fireOrder[i]! < 6)) {
        player.battery.fire(side);
        this.firing[i] = true;
      }
    }
    this.contacts.update(dt, this.vessels, this.events, (a, b) => this.wantsToBoard(a, b));
    this.lookout.update(dt, player, this.vessels, {
      visibility: this.visibility, daylight: this.day.daylight, seaState: this.waves.significantHeight, time: this.time,
    }, this.events);

    // The sea keeps what it takes: wreckage where ships went down.
    for (let i = this.vessels.length - 1; i >= 0; i--) {
      const v = this.vessels[i]!;
      if (v.state !== 'sunk' || v === player) continue;
      const t = (this.sunkTimers.get(v.id) ?? 0) + dt;
      this.sunkTimers.set(v.id, t);
      if (t > 60) this.vessels.splice(i, 1);
    }
    this.spirits(this.events.since(mark));
    for (const e of this.events.since(mark)) {
      if (e.type === 'shipSunk') {
        const v = this.vessels.find((o) => o.id === e.vessel);
        const size = v ? v.ship.sx : 1;
        this.flotsam.spawnWreck(e.x, e.z, 30 * size, Math.round(30 * size));
      } else if (e.type === 'shipSinking') {
        const v = this.vessels.find((o) => o.id === e.vessel);
        if (v) this.flotsam.spawnWreck(e.x, e.z, 20 * v.ship.sx, 8);
      } else if (e.type === 'explosion' && e.vessel >= 0 && e.size > 3) {
        this.flotsam.spawnWreck(e.x, e.z, 25, 20);
      }
    }
    const { add, remove } = this.encounters.update(dt, player, this.vessels);
    for (const v of remove) {
      const i = this.vessels.indexOf(v);
      if (i > 0) this.vessels.splice(i, 1);
    }
    this.vessels.push(...add);

    const rainWet = w.rain > 0.05 ? 1 : 0;
    const target = Math.max(rainWet, player.ship.deckWater);
    this.wetness += (target - this.wetness) * (1 - Math.exp(-dt * (target > this.wetness ? 0.25 : 0.01)));
    this.flotsam.update(dt, this.waves, this.wind, player.ship.com.x, player.ship.com.z, player.ship.vel.x, player.ship.vel.z);
    this.dev.observe(this.events.since(mark));
    // Captains perceive on their own schedule: keep about a second of events.
    for (const e of this.events.since(mark)) this.recent.push(e);
    this.recentTimes.push(...this.events.since(mark).map(() => this.time));
    while (this.recentTimes.length && this.recentTimes[0]! < this.time - 1.2) {
      this.recentTimes.shift();
      this.recent.shift();
    }

    // Ship's bell every half hour: 1..8 strikes through each four-hour watch.
    const hh = Math.floor(this.day.hours * 2);
    if (hh !== this.lastHalfHour) {
      this.lastHalfHour = hh;
      this.events.push({ type: 'bell', strikes: (hh % 8) || 8 });
    }
  }

  private wantsToBoard(a: Vessel, b: Vessel): boolean {
    if (!hostile(a, b) || !a.fighting || !boardable(b)) return false;
    if (a === this.player) return this.playerBoard > 0;
    return a.captain?.mode === 'board' && a.captain.target === b.id;
  }

  /** Captains attacking each target (for spreading out around her). */
  private attackerMap(): Map<number, number[]> {
    const m = new Map<number, number[]>();
    for (const v of this.vessels) {
      const c = v.captain;
      if (!c || c.target < 0 || !v.fighting) continue;
      const list = m.get(c.target) ?? [];
      list.push(v.id);
      m.set(c.target, list);
    }
    return m;
  }

  /**
   * The player's own crew: beat to quarters when an enemy is near (the guns
   * are laid only where the player lays them).
   */
  private playerSituation(): void {
    const p = this.player;
    const ship = p.ship;
    let nearest = Infinity, side = 0;
    for (const v of this.vessels) {
      if (v === p || !v.alive || !hostile(v, p) && !hostile(p, v)) continue;
      const d = Math.hypot(v.ship.com.x - ship.com.x, v.ship.com.z - ship.com.z);
      if (d < this.visibility * (0.3 + 0.7 * this.day.daylight) + 300 && d < nearest) {
        nearest = d;
        const l = ship.worldToLocal(v.ship.com, tmp);
        side = l.z > 0 ? 1 : -1;
      }
    }
    p.battle = nearest < 3000 || this.quarters > 0;
    // Our people's view of the odds: us against what's near enough to fight.
    let theirs = 0;
    for (const v of this.vessels) {
      if (v === p || !v.fighting || !(hostile(v, p) || hostile(p, v))) continue;
      if (Math.hypot(v.ship.com.x - ship.com.x, v.ship.com.z - ship.com.z) < 1500) theirs += power(v);
    }
    p.odds = theirs > 0 ? power(p) / theirs : 1;
    p.threatSide = nearest < 3000 ? side : 0;
    p.threatRange = nearest;
    p.gunSide = side;
  }
}

const tmp = new Vec3();
